import ICamper from "../interfaces/ICamper"
import IS3Document from "../interfaces/IS3Document"
import { post, destroy, get, patch, transformRequestData } from "./utils/requests"
import { CampImagesType } from "~/hooks/useCampImageUploader"

export type tSortDirection = "asc" | "desc"

const API_CAMPS = "/api/camps/"
const API_CAMPERS = "/api/campers/"

interface IPageOptions {
  page?: number
  per_page?: number
  sort_column?: string
  sort_direction?: tSortDirection
}

export interface ICampUpdate {
  name?: string
  color?: string
  email?: string
  phone?: string
  address?: string
  city?: string
  state?: string
  zip?: string
  bio?: string
  booking_start_date?: string
  booking_start_mm_dd?: string
  bathroom_code?: string
  gate_code?: string
  laundry_code?: string
  wifi_network?: string
  wifi_password?: string
  tax_rate?: string
  electricity_rate?: number | string
  default_invoice_memo?: string
  rules?: string
  checkin_policy?: string
  refund_policy?: string
}

const update = (id: string, data: ICampUpdate) => patch(`${API_CAMPS}${id}`, data)
const fetchImages = (id: string, type: CampImagesType = "camp-photos"): Promise<{ images: Array<IS3Document> }> =>
  get(`${API_CAMPS}${id}/images?type=${type}`)

const postImages = async (id: string, file: File, type?: CampImagesType): Promise<{ images: Array<IS3Document> }> => {
  const formData = new FormData()
  formData.append("file", file)

  const url = `${API_CAMPS}${id}/images?type=${type}`
  return await post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
}

const deleteImages = async (id: string, type?: CampImagesType, key?: string): Promise<{ message: string }> => {
  if (type === "camp-photos" && key) return await destroy(`${API_CAMPS}${id}/images?type=${type}`, { key })
  return await destroy(`${API_CAMPS}${id}/images?type=${type}`)
}

interface ICampersResponse {
  campers: ICamper[]
  total_records: number
  total_pages: number
}

const fetchCampers = (id: string, options: IPageOptions): Promise<ICampersResponse> =>
  get(`${API_CAMPS}${id}/campers`, options)

const updateCamper = (id: string, data: ICamper) => patch(`${API_CAMPERS}${id}`, data)

const findCamperByData = (data: {
  campId: string
  email?: string
  phone?: string
  license_plate?: string
}): Promise<ICampersResponse> => {
  const params = new URLSearchParams()
  if (data.email) params.append("email", data.email)
  if (data.phone) params.append("phone", data.phone)
  if (data.license_plate) params.append("license_plate", data.license_plate)
  return get(`${API_CAMPS}${data.campId}/campers?${params.toString()}`)
}

const findCamper = (id: string, options: { includePaymentMethods?: boolean } = {}): Promise<{ camper: ICamper }> =>
  get(`${API_CAMPERS}${id}`, transformRequestData(options))

const Camps = {
  fetchCampers,
  findCamperByData,
  findCamper,
  update,
  updateCamper,
  fetchImages,
  postImages,
  deleteImages,
}

export default Camps
